import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ConfirmModal } from "../components/common";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import applicationService, {
  ApplicationListItem,
  ApplicationStatus,
} from "../services/api/applicationService";

const MyApplications: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [applications, setApplications] = useState<ApplicationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAppId, setDeletingAppId] = useState<string | null>(null);

  console.log(applications, 'applications');
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/signin?redirect=/my-applications");
      return;
    }
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, page]);

  const loadApplications = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await applicationService.getApplications({ page, limit: 10 });
      setApplications(response.applications);
      setTotalPages(response.pagination.pages);
    } catch (err: any) {
      setError(err.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeletingAppId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingAppId) return;
    setShowDeleteModal(false);

    try {
      await applicationService.deleteApplication(deletingAppId);
      loadApplications();
    } catch (err: any) {
      showError(err.message || "Failed to delete application");
    } finally {
      setDeletingAppId(null);
    }
  };

  const getStatusColor = (status: ApplicationStatus): string => {
    const colors: Record<ApplicationStatus, string> = {
      DRAFT: "bg-gray-100 text-gray-800",
      PENDING_REVIEW: "bg-orange-100 text-orange-800",
      APPROVED: "bg-green-100 text-green-800",
      SUBMITTED: "bg-blue-100 text-blue-800",
      UNDER_REVIEW: "bg-yellow-100 text-yellow-800",
      ACCEPTED: "bg-green-100 text-green-800",
      REJECTED: "bg-red-100 text-red-800",
      WAITLISTED: "bg-purple-100 text-purple-800",
      WITHDRAWN: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status: ApplicationStatus): string => {
    const icons: Record<ApplicationStatus, string> = {
      DRAFT: "📝",
      PENDING_REVIEW: "⏳",
      APPROVED: "✅",
      SUBMITTED: "📤",
      UNDER_REVIEW: "🔍",
      ACCEPTED: "✅",
      REJECTED: "❌",
      WAITLISTED: "⏳",
      WITHDRAWN: "🚫",
    };
    return icons[status] || "📄";
  };

  if (loading && (!applications || applications.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Applications</h1>
          <p className="text-gray-600">
            Track and manage your university applications
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Applications List */}
        {!applications || applications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No Applications Yet
            </h2>
            <p className="text-gray-600 mb-6">
              Start your journey by applying to your dream university
            </p>
            <Link
              to="/courses"
              className="inline-block px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Browse Courses
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <div
                key={app.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getStatusIcon(app.status)}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {app.course.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Application #{app.applicationNumber}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <span className="text-sm text-gray-600">Level:</span>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {app.course.level}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Intake:</span>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {app.intake?.name || "Not selected"}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Applied:</span>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {new Date(app.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {app.givenNames && app.familyName && (
                      <div className="mt-3">
                        <span className="text-sm text-gray-600">Applicant:</span>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {app.givenNames} {app.familyName}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-3 ml-6">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                        app.status
                      )}`}
                    >
                      {app.status.replace("_", " ")}
                    </span>

                    <div className="flex gap-2">
                      <Link
                        to={`/applications/${app.id}`}
                        className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                      >
                        View Details
                      </Link>

                      {app.status === "DRAFT" && (
                        <>
                          <Link
                            to={`/apply/${app.courseId}?applicationId=${app.id}`}
                            className="px-4 py-2 text-sm border border-cyan-600 text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors"
                          >
                            Continue
                          </Link>
                          <button
                            onClick={() => handleDelete(app.id)}
                            className="px-4 py-2 text-sm border border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Delete
                          </button>
                        </>
                      )}

                      {app.status === "APPROVED" && (
                        <Link
                          to={`/applications/${app.id}`}
                          className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          Pay Now
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Application"
        message="Are you sure you want to delete this draft application? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => { setShowDeleteModal(false); setDeletingAppId(null); }}
        variant="danger"
      />
    </div>
  );
};

export default MyApplications;
